/*
 * Copyright (c) 2022 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import { Asset, AssetUtils } from '@wit/vtv-x-foundation-lib';
import React, { useCallback, useState } from 'react';
import translate from 'services/i18n/translate';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
// eslint-disable-next-line import/extensions
import ColorThief from 'colorthief/dist/color-thief.umd.js';

import { ProgressBar } from '..';
import ImageLoader from '../ImageLoader';
import Badge from '../Badge';

interface Props {
  asset: Asset;
  onAssetClick: (asset: Asset) => void;
  showSeriesInfo?: boolean;
  ctxId: number | string;
}

export default function Small({ onAssetClick, asset, showSeriesInfo, ctxId }: Props): JSX.Element {
  const cardId = `${ctxId}-${asset.id}`;
  const [infoBackgroundColor, setInfoBackgroundColor] = useState<string | undefined>();
  const [isMouseOver, setIsMouseOver] = useState(false);
  const [imageRef, setImageRef] = useState<React.RefObject<HTMLImageElement>>();

  const badgesClass = () => {
    switch (asset.badges?.length) {
      case 0:
        return 'no-badge';
      case 1:
        return 'one-badge';
      case 2:
        return 'two-badge';
      case 3:
        return 'three-badge';
      default:
        return 'no-badge';
    }
  };

  const onImageLoad = (ref?: React.RefObject<HTMLImageElement>) => {
    if (ref) {
      setImageRef(ref);
    }
  };

  const clicked = () => {
    onAssetClick(asset);
  };

  const mouseOver = useCallback(() => {
    setIsMouseOver(true);
    if (imageRef?.current && infoBackgroundColor === undefined) {
      const colorThief = new ColorThief();
      const palette = colorThief.getPalette(imageRef.current);
      if (palette && palette.length) {
        const rgb = {
          r: (palette[4][0] * 0.75).toFixed(0),
          g: (palette[4][1] * 0.75).toFixed(0),
          b: (palette[4][2] * 0.75).toFixed(0),
        };
        setInfoBackgroundColor(`rgb(${rgb.r},${rgb.g},${rgb.b})`);
      }
    }
  }, [imageRef, infoBackgroundColor]);

  const isEpg = asset && AssetUtils.isEpg(asset);
  const isEpgWithChannelLogo = isEpg && asset.logo?.color?.uri;
  const cardImage = asset?.images.card || (isEpg && asset.logo?.color?.uri) || '';

  const isEpisode =
    asset && (AssetUtils.isEpisode(asset) || AssetUtils.isTvAsset(asset)) && AssetUtils.hasValidSeriesId(asset);
  const hasValidSeasonNumber = asset && AssetUtils.hasValidSeasonNumber(asset);
  const hasValidEpisodeNumber = asset && AssetUtils.hasValidEpisodeNumber(asset);

  const title = isEpisode && AssetUtils.isRecording(asset) && !showSeriesInfo ? asset.seriesName : asset.title;

  return (
    <div
      onClick={clicked}
      onMouseOver={mouseOver}
      onMouseOut={() => setIsMouseOver(false)}
      style={{ width: '100%', display: 'flex' }}
      className="content-card-cell small"
      key={asset.key}
      id={`Card___${cardId}`}
    >
      <a
        className={`context-switch content-card small not-scheduled ${
          showSeriesInfo && isEpisode ? 'double-line' : 'single-line'
        }`}
        style={{ width: '100%', height: 'auto' }}
        title={title}
      >
        <div className="border-radius-container card-wrapper">
          <ImageLoader
            src={cardImage}
            className={`image-loader-cover image-loader-center image-loader-rail content-card-image ${
              showSeriesInfo ? 'series-info' : ''
            }`}
            fallbackClass="content-card-image fallback-content-card-image image-loader"
            successClass={`image-loader-positioning ${
              !asset?.images.card && isEpgWithChannelLogo ? 'card-image-channel-logo' : ''
            }`}
            onSuccess={onImageLoad}
            bemModifier={cardId}
          />

          <div className="content-card-info" style={{ backgroundColor: isMouseOver ? infoBackgroundColor : undefined }}>
            <div className={`warpper-label ${isEpisode && 'seriesinfo'}`}>
              <div className={`name-holder ${badgesClass()}`}>
                {showSeriesInfo && isEpisode ? (
                  <p id={`Card_seriesInfo___${cardId}`} className="seriesinfo">
                    {asset && hasValidSeasonNumber && (
                      <span>{translate['programInfo.moreepisodes.seasonInitial']}</span>
                    )}
                    {asset && hasValidSeasonNumber && (
                      <span className="season-number-label" id={`Card_season___${cardId}`}>
                        {asset.seasonNumber}
                      </span>
                    )}
                    {asset && hasValidSeasonNumber && hasValidEpisodeNumber && <span>, </span>}
                    {asset && hasValidEpisodeNumber && (
                      <span>{translate['programInfo.moreepisodes.episodeInitial']}</span>
                    )}
                    {asset && hasValidEpisodeNumber && (
                      <span className="season-number-label" id={`Card_episode___${cardId}`}>
                        {asset.episodeNumber}
                      </span>
                    )}
                  </p>
                ) : null}
                <h3 id={`Card_title___${cardId}`}>{title}</h3>
              </div>

              {asset.badges &&
                asset.badges.map((badge) => {
                  return <Badge badge={badge} bemBlock="Card" bemModifierPrefix={cardId} key={badge.icon} />;
                })}
            </div>

            {asset?.progress !== undefined ? (
              <ProgressBar thin isActive={isMouseOver} percentage={asset.progress} bemModifier={cardId} />
            ) : null}
          </div>
        </div>
      </a>
    </div>
  );
}
